import { type FunctionComponent } from 'preact'
import { html } from 'htm/preact'
import { signal, computed, batch } from '@preact/signals'
import '@substrate-system/button'
import type { AppState } from '../../state.js'
import {
    ToolPage,
    Disclaimer,
    ToolWorkbench,
} from '../../components/tool-page.js'
import {
    calcAuditChecklist,
    CHECKLIST_QUESTIONS,
    CHECKLIST_CATEGORIES,
    type RiskLevel,
    type ChecklistAnswer,
    type AuditChecklistResult,
    type Urgency,
} from '../../util/epa-audit-checklist.js'
import { exportPdf, type PdfSection } from '../../util/pdf.js'
import { EM_DASH } from '../../constants.js'

const STORAGE_KEY = 'hvaclog-epa-audit-checklist'

const RISK_LABELS:Record<RiskLevel, string> = {
    low: 'Low Risk ' + EM_DASH + ' practices appear generally sound.',
    moderate: 'Moderate Risk ' + EM_DASH + ' some compliance gaps identified; improvement recommended.',
    high: 'High Risk ' + EM_DASH + ' significant compliance gaps present; action required.',
    critical: 'Critical Risk ' + EM_DASH + ' major compliance failures detected; immediate action required.',
}

const URGENCY_LABELS:Record<Urgency, string> = {
    critical: 'Immediate',
    high: 'Within 30 days',
    moderate: 'Within 90 days',
    low: 'Recommended',
}

const URGENCY_LEVEL:Record<Urgency, string> = {
    critical: 'critical',
    high: 'high',
    moderate: 'moderate',
    low: 'low',
}

function loadSaved ():{
    answers:Record<string, ChecklistAnswer>;
    companyName:string;
    } {
    try {
        const raw = localStorage.getItem(STORAGE_KEY)
        if (!raw) return { answers: {}, companyName: '' }
        return JSON.parse(raw) as {
            answers:Record<string, ChecklistAnswer>;
            companyName:string;
        }
    } catch {
        return { answers: {}, companyName: '' }
    }
}

function saveState (
    answers:Record<string, ChecklistAnswer>,
    companyName:string
):void {
    try {
        localStorage.setItem(
            STORAGE_KEY,
            JSON.stringify({ answers, companyName })
        )
    } catch {
        // storage not available
    }
}

const saved = loadSaved()
const answers = signal<Record<string, ChecklistAnswer>>(saved.answers)
const companyName = signal(saved.companyName)
const submitted = signal(false)

const result = computed(():AuditChecklistResult | null => {
    if (!submitted.value) return null
    return calcAuditChecklist(answers.value)
})

function handleAnswer (qId:string, value:ChecklistAnswer):void {
    const next = { ...answers.value, [qId]: value }
    answers.value = next
    saveState(next, companyName.value)
}

function handleCompanyName (e:Event):void {
    const val = (e.target as HTMLInputElement).value
    companyName.value = val
    saveState(answers.value, val)
}

function handleSubmit (e:Event):void {
    e.preventDefault()
    submitted.value = true
}

function handleReset ():void {
    batch(() => {
        answers.value = {}
        companyName.value = ''
        submitted.value = false
    })
    saveState({}, '')
}

function answeredCount ():number {
    return CHECKLIST_QUESTIONS.filter(
        q => answers.value[q.id] !== undefined
    ).length
}

function handleDownloadPdf (e:Event):void {
    e.preventDefault()
    const r = result.value
    if (!r) return

    const sections:PdfSection[] = []

    if (companyName.value) {
        sections.push({
            heading: 'Company',
            rows: [{ label: 'Company Name', value: companyName.value }],
        })
    }

    sections.push({
        heading: 'Overall Result',
        rows: [
            {
                label: 'Risk Level',
                value: r.riskLevel.toUpperCase(),
            },
            {
                label: 'Score',
                value: `${r.totalScore.toFixed(1)} / 100`,
            },
            {
                label: 'Summary',
                value: RISK_LABELS[r.riskLevel],
            },
        ],
    })

    sections.push({
        heading: 'Category Scores',
        rows: r.categoryScores.map(c => ({
            label: c.label,
            value: c.applicableCount > 0 ?
                `${c.failCount} of ${c.applicableCount} items failed` :
                'Not assessed',
        })),
    })

    if (r.actionItems.length > 0) {
        sections.push({
            heading: 'Action Plan',
            rows: r.actionItems.map((item, i) => ({
                label: `${i + 1}. [${URGENCY_LABELS[item.urgency]}]`,
                value: item.text,
            })),
        })
    }

    exportPdf({
        title: 'EPA Audit Readiness Checklist Report',
        sections,
        footer:
            'Grounded in 40 CFR Part 82 Subpart F. For educational and ' +
            'operational planning purposes only. Not legal advice. Does not ' +
            'predict enforcement outcomes.',
    })
}

export const EpaAuditChecklist:FunctionComponent<{
    state:AppState
}> = function EpaAuditChecklist () {
    const r = result.value
    const answered = answeredCount()

    return html`
        <${ToolPage}
            title="EPA Audit Readiness Checklist"
            description="Complete this 28-question checklist to assess your
                organization's readiness for an EPA Section 608 audit under
                40 CFR Part 82 Subpart F."
        >
            <${ToolWorkbench}
                inputsLabel="Checklist"
                resultsLabel="Readiness"
                emptyHint="Complete the checklist, then select Evaluate
                    Readiness."
            >
                <form class="tool-form" onSubmit=${handleSubmit}>

                <div class="tool-form-section">
                    <p class="tool-section-label">Company</p>
                    <div class="tool-field">
                        <label
                            class="tool-field-label"
                            for="epa-company-name"
                        >
                            Company Name (optional)
                        </label>
                        <input
                            id="epa-company-name"
                            class="tool-input"
                            type="text"
                            value=${companyName.value}
                            onInput=${handleCompanyName}
                            placeholder="Your company name"
                        />
                    </div>
                </div>

                ${CHECKLIST_CATEGORIES.map(cat => html`
                    <div class="tool-form-section" key=${cat.id}>
                        <p class="tool-section-label">${cat.label}</p>
                        ${CHECKLIST_QUESTIONS.filter(
                            q => q.category === cat.id
                        ).map(q => html`
                            <div class="tool-field" key=${q.id}>
                                <p class="tool-field-label">${q.text}</p>
                                <div
                                    class="tool-choices"
                                    role="radiogroup"
                                    aria-label=${q.text}
                                >
                                    ${(['yes', 'no', 'na'] as const).map(
                                        val => html`
                                        <label
                                            class="tool-choice"
                                            key=${val}
                                        >
                                            <input
                                                type="radio"
                                                name=${q.id}
                                                value=${val}
                                                checked=${
                                                    answers.value[q.id]
                                                        === val
                                                }
                                                onChange=${() =>
                                                    handleAnswer(
                                                        q.id,
                                                        val
                                                    )
                                                }
                                            />
                                            ${val === 'yes' ? 'Yes' :
                                                val === 'no' ? 'No' :
                                                'N/A'
                                            }
                                        </label>
                                    `)}
                                </div>
                            </div>
                        `)}
                    </div>
                `)}

                <div class="tool-actions">
                    <substrate-button class="primary" type="submit">
                        Evaluate Readiness${answered <
                            CHECKLIST_QUESTIONS.length ?
                            ` (${answered}/${
                                CHECKLIST_QUESTIONS.length
                            } answered)` :
                            ''
                        }
                    </substrate-button>
                    <substrate-button
                        type="button"
                        onClick=${(e:Event) => {
                            e.preventDefault()
                            handleReset()
                        }}
                    >
                        Reset
                    </substrate-button>
                </div>
            </form>

            ${r && html`
                <div class="tool-readout">
                    <span
                        class="tool-badge"
                        data-level=${r.riskLevel}
                    >
                        ${r.riskLevel} risk
                    </span>

                    <p class="tool-readout-detail">
                        Score: ${r.totalScore.toFixed(1)} / 100
                    </p>
                    <p class="tool-readout-detail">
                        ${RISK_LABELS[r.riskLevel]}
                    </p>

                    <div class="tool-metrics">
                        ${r.categoryScores.map(c => html`
                            <div class="tool-metric" key=${c.id}>
                                <p class="tool-metric-label">${c.label}</p>
                                <p class="tool-metric-value">
                                    ${c.applicableCount > 0 ?
                                        `${c.failCount}/${
                                            c.applicableCount
                                        } failed` :
                                        'N/A'
                                    }
                                </p>
                            </div>
                        `)}
                    </div>

                    ${r.actionItems.length > 0 && html`
                        <div class="tool-recs">
                            <h3>Action Plan</h3>
                            <ol>
                                ${r.actionItems.map(item => html`
                                    <li key=${item.questionId}>
                                        <span
                                            class="tool-badge"
                                            data-level=${
                                                URGENCY_LEVEL[item.urgency]
                                            }
                                        >
                                            ${URGENCY_LABELS[item.urgency]}
                                        </span>
                                        ${item.text}
                                    </li>
                                `)}
                            </ol>
                        </div>
                    `}

                    <div class="tool-actions">
                        <substrate-button
                            type="button"
                            onClick=${handleDownloadPdf}
                        >
                            Download PDF Report
                        </substrate-button>
                    </div>
                </div>
            `}
            </${ToolWorkbench}>

            <${Disclaimer} />
        </${ToolPage}>
    `
}
